/**
 * THE TOLERANCE BENEATH AN ANSWER, for the five blocks that grade a number against one.
 *
 * Numerical, Slider, Compass, Tilt and Rotation each hand-rolled these rows, identically enough
 * that every fix had to be made five times — and Rotation is the proof that it does not get made
 * five times: it was still carrying the pre-change layout, `±` outside a fixed 80px box, while
 * the other four had moved on. This is that markup, once.
 *
 * ## Why the tolerance has a row of its own
 *
 * It used to share the answer's line, and that was right while an answer was a bare number:
 * seeing `10 ± 1` on one line beats seeing it on two. An answer holds an EQUATION, and since
 * answer references arrived it holds chips naming other tasks' answers, which are wide — the
 * answer field was left whatever a tolerance box did not take, and a single chip filled it. So
 * the answer keeps its line and this sits beneath it: the adjacency the old layout bought
 * horizontally, rotated.
 *
 * `±` lives INSIDE the box as a prefix, the way `−` and `+` already did in the asymmetric pair,
 * which is what puts the box's own left edge under the answer above rather than indenting it by
 * a glyph.
 *
 * ## What the caller still owns
 *
 * The absolute/percentage control is passed in as `trailing` rather than built here, because the
 * blocks disagree about it in ways that are not cosmetic: Compass, Tilt and Rotation have no
 * percentage mode at all (no privileged zero, so a percentage of an angle means nothing), and
 * Numerical disables it on an integer-only block. The symmetry toggle beside it is the same for
 * everyone, so `SymmetryToggle` is here.
 */
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { NumberField } from '@/components/ui/number-field';

/*
 * THE INPUTS TAKE THE ROW; the type controls beside them do not.
 *
 * A tolerance is a short number, so for a while these were a fixed 96px and the row carried its
 * slack as empty space. Full width reads better and costs nothing: the controls are a fixed
 * square each, so one long box in symmetric mode and two equal ones in asymmetric are what is
 * left, and the row is the same shape in both.
 *
 * `min-w-0` is load-bearing with `flex-1`: without it a flex child refuses to shrink below its
 * content and the asymmetric pair push the controls off the end of a narrow row.
 */
const TOLERANCE_W = 'min-w-0 flex-1';

/**
 * The shape of a type control, square while it rides a tolerance row and stretched on a row of
 * its own.
 *
 * Exported because two of the five blocks build their own absolute/percentage button and it has
 * to match the one beside it. The square settles two things: the symmetry toggle stops resizing
 * as it swaps `±` for `−/+` — 30px against 45px, a control changing size without changing what
 * it does — and the controls stop competing with the inputs for the row.
 *
 * The explicit height matches the fields, and goes with `leading-none` on a glyph: `font-mono`'s
 * normal line-height made the toggle 38px against the 34px beside it, so it sat proud of its row.
 */
export function toleranceControlShape(compact: boolean) {
  return `${compact ? SQUARE_SHAPE : 'flex-1'} `
    + 'focus-visible:ring-2 focus-visible:ring-ws-primary focus-visible:ring-offset-1';
}

/**
 * The geometry, for the one caller that is NOT a `Button`.
 *
 * Numerical builds its two controls from a hand-rolled `<button>` that predates the shared
 * component and differs from it (`rounded-lg`, its own hover), so it cannot take `size`. It
 * takes the class string instead, and because it has no competing `px-4` of its own, `px-0`
 * actually applies there — which is why that one block never showed the wrapped glyph.
 */
const SQUARE_SHAPE = 'h-[2.125rem] w-11 shrink-0 px-0 whitespace-nowrap';

/**
 * THE WIDTH OF THE ANSWER ROW'S DELETE BUTTON, held open on the tolerance row beneath it.
 *
 * With one answer there is no delete button and the type controls sit at the end of this row, so
 * everything lines up by itself. Add a second answer and both of those reverse at once: a delete
 * button appears on the answer row, and the type controls leave for a labelled row of their own —
 * so the tolerance box ran the full width while the answer above it stopped 44px short, and the
 * box describing the answer stuck out further than the answer did.
 *
 * `h-9 w-9` is `IconButton size='md'`, which is the button being reserved for; the test holds the
 * two together.
 */
const DELETE_SLOT = 'h-9 w-9 shrink-0';

/** Which of the three boxes a flag is about. Symmetric shows `value`; asymmetric the other two. */
export interface ToleranceFlags {
  value?: boolean;
  below?: boolean;
  above?: boolean;
}

export function ToleranceRow({
  idPrefix, asymmetric, grammar, percent,
  toleranceValue, toleranceBelow, toleranceAbove, onChange,
  invalid, warn, describedBy, onFocus, trailing, reserveDelete, ownerLabel,
}: {
  /** Field ids are `${idPrefix}.toleranceValue` and so on — the answer's own id, in every caller. */
  idPrefix: string;
  asymmetric: boolean;
  /** `decimal` on the angle blocks, whose domain is bounded and whose tolerance is a handful of
   *  degrees, so an exponent has no use; `scientific` where the domain is unbounded; `integer`
   *  where the block forbids decimals. Per-domain and deliberate. */
  grammar: 'decimal' | 'scientific' | 'integer';
  /** Shows the `%` suffix. Absent on the blocks with no percentage mode to be in. */
  percent?: boolean;
  toleranceValue: string;
  toleranceBelow: string;
  toleranceAbove: string;
  onChange: (patch: { toleranceValue?: string; toleranceBelow?: string; toleranceAbove?: string }) => void;
  /** Red: this number is wrong. */
  invalid?: ToleranceFlags;
  /** Amber: this number is allowed but over a cap the block will clamp. */
  warn?: ToleranceFlags;
  /** The id of the text explaining the warning, pointed at by whichever box is warning. */
  describedBy?: string;
  onFocus?: () => void;
  /** The block's type controls, when there is one answer and this row is carrying them. */
  trailing?: React.ReactNode;
  /** The answer row above has a delete button, so hold its width open here — see `DELETE_SLOT`.
   *  Ignored when `trailing` fills the same space. */
  reserveDelete?: boolean;
  /**
   * WHAT THIS TOLERANCE BELONGS TO, where one surface carries more than one of these rows.
   *
   * Every caller but one has a single tolerance, and "Tolerance (±)" names it unambiguously.
   * Numerical's error box puts a second row a tab away from the answer's, and two controls
   * answering to the same words is exactly what the naming rules forbid — so the owner is named
   * and the row's own word is kept, which is what label-in-name asks for.
   */
  ownerLabel?: string;
}) {
  const { t } = useTranslation();
  /** `Tolerance (±)` alone, or `Uncertainty tolerance (±)` where a sibling row would collide. */
  const named = (key: string) => (ownerLabel
    ? t('blocks.tolerance.toleranceOwnedAriaLabel', { owner: ownerLabel, label: t(key) })
    : t(key));
  const common = {
    grammar, onFocus,
    placeholder: '0',
    suffix: percent ? '%' : undefined,
    className: TOLERANCE_W,
  } as const;

  return (
    <div className='flex items-center gap-2'>
      {asymmetric ? (
        <>
          <NumberField {...common}
            fieldId={`${idPrefix}.toleranceBelow`}
            value={toleranceBelow}
            onChange={v => onChange({ toleranceBelow: v })}
            hasError={invalid?.below}
            hasWarning={warn?.below}
            describedBy={warn?.below ? describedBy : undefined}
            prefix='−'
            ariaLabel={named('blocks.tolerance.toleranceBelowAriaLabel')} />
          <NumberField {...common}
            fieldId={`${idPrefix}.toleranceAbove`}
            value={toleranceAbove}
            onChange={v => onChange({ toleranceAbove: v })}
            hasError={invalid?.above}
            hasWarning={warn?.above}
            describedBy={warn?.above ? describedBy : undefined}
            prefix='+'
            ariaLabel={named('blocks.tolerance.toleranceAboveAriaLabel')} />
        </>
      ) : (
        <NumberField {...common}
          fieldId={`${idPrefix}.toleranceValue`}
          value={toleranceValue}
          onChange={v => onChange({ toleranceValue: v })}
          hasError={invalid?.value}
          hasWarning={warn?.value}
          describedBy={warn?.value ? describedBy : undefined}
          prefix='±'
          ariaLabel={named('blocks.tolerance.toleranceAriaLabel')} />
      )}
      {trailing ?? (reserveDelete ? <span aria-hidden='true' className={DELETE_SLOT} /> : null)}
    </div>
  );
}

/**
 * Symmetric or asymmetric, the one type control every tolerance block has.
 *
 * The glyph is `aria-hidden`: unhidden it announced as "±" or "−/+", punctuation read aloud
 * where a name should be, so `aria-label` carries the name and the glyph is decoration.
 */
export function SymmetryToggle({
  asymmetric, onToggle, compact, fieldId = 'toleranceSymmetry',
}: {
  asymmetric: boolean;
  onToggle: () => void;
  /** Riding an answer's tolerance row rather than sitting on its own labelled one. */
  compact: boolean;
  /** Slider's two handles each get their own, so it namespaces the id. */
  fieldId?: string;
}) {
  const { t } = useTranslation();
  return (
    <Button
      /* `size='square'` rather than a width in `className`: the shared component's own `px-4`
         beats a caller's `px-0`, which is what crushed this glyph onto three lines. */
      size={compact ? 'square' : 'md'}
      data-field-id={fieldId}
      aria-pressed={asymmetric}
      aria-label={t('blocks.tolerance.toleranceSymmetryAriaLabel')}
      onClick={onToggle}
      className={compact ? 'focus-visible:ring-2 focus-visible:ring-ws-primary focus-visible:ring-offset-1' : toleranceControlShape(false)}
    >
      <span aria-hidden='true' className='font-mono font-semibold leading-none'>
        {asymmetric ? <><sup>−</sup><span className='mx-px opacity-50'>/</span><sub>+</sub></> : '±'}
      </span>
    </Button>
  );
}

/**
 * The type controls on a labelled row of their own.
 *
 * Only reached when a block has MORE THAN ONE answer. They are block-level — one `toleranceMode` and
 * one `toleranceSymmetry` govern every answer — while the boxes above are per answer, so with several
 * answers one control cannot sit on five rows, and putting it on the first would attach a block
 * setting to one answer. With a single answer they ride that answer's tolerance row instead and
 * the whole setup is two lines.
 *
 * The label lives here and nowhere else: on the merged row there is no space for it beside two
 * boxes, and `±` and `−`/`+` already name the field.
 */
export function ToleranceTypeRow({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className='flex items-center gap-2'>
      <span className='shrink-0 text-sm font-medium text-ws-text'>
        {t('blocks.tolerance.errorLabel')}
      </span>
      {children}
    </div>
  );
}
